import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api/client";
import { t, stateLabel, stateTone, type Tone } from "../i18n";

/* ---------- Wordmark ---------- */
const TAIL_D1 = "M8 22 L33 70";
const TAIL_D2 = "M58 22 L20 92 C8 114 30 124 62 118 C92 112 118 106 136 100";

export function Wordmark({ className = "", ...rest }: { className?: string } & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={`wm ${className}`} aria-label={t.brand.name} role="img" {...rest}>
      <span aria-hidden="true">Fox</span>
      <svg className="yg" viewBox="0 0 62 70" aria-hidden="true">
        <path d={TAIL_D1} />
        <path d={TAIL_D2} />
        <circle cx="150" cy="95" r="7" />
      </svg>
      <span aria-hidden="true">Pay</span>
    </span>
  );
}

/** O "y" com cauda. `wag` dispara a animação única. */
export function YSymbol({ size = 24, wag = false, className = "", style }: { size?: number; wag?: boolean; className?: string; style?: React.CSSProperties }) {
  return (
    <svg
      className={`ysym ${wag ? "wag go" : "wag"} ${className}`}
      viewBox="0 0 160 125"
      width={size}
      height={Math.round(size * 0.78)}
      aria-hidden="true"
      style={style}
    >
      <path d={TAIL_D1} />
      <path d={TAIL_D2} />
      <circle cx="150" cy="95" r="7" />
    </svg>
  );
}

/* ---------- Ícones (traço 1.75, sem emoji) ---------- */
export const Icon = {
  home: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 11 L12 4 L20 11 V20 H14 V14 H10 V20 H4 Z" /></svg>,
  arrow: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12 H20 M14 6 L20 12 L14 18" /></svg>,
  people: () => <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.5" /><path d="M3 20 C3 16 6 14 9 14 C12 14 15 16 15 20" /><circle cx="17" cy="9" r="2.5" /><path d="M16 14 C19 14 21 16 21 19" /></svg>,
  doc: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3 H14 L19 8 V21 H6 Z M14 3 V8 H19 M9 13 H16 M9 17 H16" /></svg>,
  gear: () => <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3.2" /><path d="M12 2.5 V5 M12 19 V21.5 M2.5 12 H5 M19 12 H21.5 M5.3 5.3 L7 7 M17 17 L18.7 18.7 M18.7 5.3 L17 7 M7 17 L5.3 18.7" /></svg>,
  flag: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 21 V4 H18 L15 8 L18 12 H5" /></svg>,
  plus: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5 V19 M5 12 H19" /></svg>,
  download: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4 V15 M7 11 L12 16 L17 11 M5 20 H19" /></svg>,
  check: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5 L10 17 L19 7" /></svg>,
  clip: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 12 L15 5 A3 3 0 0 1 19 9 L11 17 A5 5 0 0 1 4 10 L12 2" /></svg>,
  lock: () => <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11 V8 A4 4 0 0 1 16 8 V11" /></svg>,
  copy: () => <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15 V6 A1 1 0 0 1 6 5 H15" /></svg>,
  back: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 L8 12 L15 19" /></svg>,
  more: () => <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="6" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="18" cy="12" r="1.5" /></svg>,
  refund: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14 L4 9 L9 4 M4 9 H15 A5 5 0 0 1 15 19 H10" /></svg>,
  pen: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20 L8 19 L19 8 L16 5 L5 16 Z M14 7 L17 10" /></svg>,
  retry: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12 A8 8 0 1 1 17.5 6.2 M17.5 3 V6.5 H14" /></svg>,
  list: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7 H19 M5 12 H19 M5 17 H19" /></svg>,
  bell: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 16 V11 a6 6 0 0 1 12 0 v5 l2 2 H4 Z M10 20 a2 2 0 0 0 4 0" /></svg>,
  logout: () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 4 H5 V20 H10 M14 8 L19 12 L14 16 M19 12 H9" /></svg>,
};

/* ---------- Pill de estado ---------- */
export function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={`pill ${tone}`}>
      <i aria-hidden="true" />
      {children}
    </span>
  );
}
export function StatePill({ state, suffix }: { state: string; suffix?: string }) {
  return (
    <Pill tone={stateTone(state)}>
      {stateLabel(state)}
      {suffix ? ` · ${suffix}` : ""}
    </Pill>
  );
}

/* ---------- Loading / erro / vazio ---------- */
export function Spinner({ label }: { label?: string }) {
  return (
    <span className="loading" role="status" aria-live="polite">
      <span className="spin" aria-hidden="true" />
      {label ?? t.common.loading}
    </span>
  );
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return t.common.unauthorized;
    if (e.status === 403) return t.common.forbidden;
    return e.message || t.common.errorGeneric;
  }
  if (e instanceof Error) return e.message || t.common.errorGeneric;
  return t.common.errorGeneric;
}

export function ErrorBox({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div className="errbox" role="alert">
      <div>
        <b>{title ?? t.common.errorTitle}.</b> {errorMessage(error)}
      </div>
      {onRetry && (
        <div>
          <button type="button" className="btn ghost sm" onClick={onRetry}>
            {t.common.retry}
          </button>
        </div>
      )}
    </div>
  );
}

export function Empty({ title, body, action }: { title?: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      {title && <h3>{title}</h3>}
      <div>{body ?? t.common.empty}</div>
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

/* ---------- Toast ---------- */
interface ToastCtx {
  toast: (msg: string) => void;
}
const ToastContext = createContext<ToastCtx>({ toast: () => undefined });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const toast = useCallback((m: string) => {
    setMsg(m);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setMsg(null), 2600);
  }, []);
  const value = useMemo(() => ({ toast }), [toast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      {msg && (
        <div className="toast" role="status" aria-live="polite">
          {msg}
        </div>
      )}
    </ToastContext.Provider>
  );
}
export function useToast() {
  return useContext(ToastContext).toast;
}

/* ---------- Field ---------- */
interface FieldProps {
  label: string;
  hint?: string;
  error?: string | null;
  children: (id: string, describedBy: string | undefined) => ReactNode;
  className?: string;
}
export function Field({ label, hint, error, children, className = "" }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`field ${className}`}>
      <label htmlFor={id}>{label}</label>
      {children(id, describedBy)}
      {hint && !error && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="error" id={errId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/* ---------- Modal ---------- */
export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input,select,textarea,button");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus();
    };
  }, [onClose]);
  return (
    <div
      className="modal-bg"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="row between">
          <h2>{title}</h2>
          <button type="button" className="btn ghost xs" onClick={onClose}>
            {t.common.close}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- JSON dobrável ---------- */
export function JsonView({ title, value, open = false }: { title: string; value: unknown; open?: boolean }) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(value ?? null, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);
  return (
    <details className="fold" open={open}>
      <summary>{title}</summary>
      <pre className="jsonview">{text}</pre>
    </details>
  );
}

/* ---------- KPI ---------- */
export function Kpi({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "go" | "ok" | "crit" }) {
  return (
    <div className="card kpi">
      <span className="lbl">{label}</span>
      <span className={`v ${tone ?? ""}`}>{value}</span>
      {sub !== undefined && <span className="sub">{sub}</span>}
    </div>
  );
}

/* ---------- Utilidades ---------- */
export function usePrefersReducedMotion(): boolean {
  const [r, setR] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false));
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fn = () => setR(mq.matches);
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);
  return r;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback abaixo */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
